import type { FlightRoute } from '@/shared/types'
import { AIRPORTS_BY_ID } from './airports'

interface RouteSeed {
  code: string
  name: string
  type: FlightRoute['type']
  from: string
  to: string
  waypoints?: { lat: number; lon: number; alt: number; name: string }[]
}

const SEEDS: RouteSeed[] = [
  { code: 'RT-N01', name: 'Nội Bài → Yên Bái (huấn luyện)', type: 'training', from: 'ap-1', to: 'ap-2' },
  { code: 'RT-N02', name: 'Nội Bài → Kép', type: 'standard', from: 'ap-1', to: 'ap-3' },
  { code: 'RT-N03', name: 'Cát Bi → Kép', type: 'standard', from: 'ap-4', to: 'ap-3' },
  { code: 'RT-N04', name: 'Tuần tra biển Đông Bắc', type: 'tactical', from: 'ap-4', to: 'ap-4' },
  { code: 'RT-N05', name: 'Yên Bái → Nội Bài (chuyển sân)', type: 'standard', from: 'ap-2', to: 'ap-1' },

  { code: 'RT-C01', name: 'Đà Nẵng → Phù Cát (huấn luyện)', type: 'training', from: 'ap-6', to: 'ap-7' },
  { code: 'RT-C02', name: 'Đà Nẵng → Pleiku', type: 'standard', from: 'ap-6', to: 'ap-8' },
  { code: 'RT-C03', name: 'Tuần tra biển Đông Trung Trung Bộ', type: 'tactical', from: 'ap-6', to: 'ap-6' },
  { code: 'RT-C04', name: 'Phù Cát → Phan Rang', type: 'standard', from: 'ap-7', to: 'ap-10' },
  { code: 'RT-C05', name: 'Cam Ranh → Phù Cát', type: 'training', from: 'ap-11', to: 'ap-7' },
  { code: 'RT-C06', name: 'Vinh → Đà Nẵng (chuyển sân)', type: 'standard', from: 'ap-9', to: 'ap-6' },

  { code: 'RT-S01', name: 'Tân Sơn Nhất → Biên Hòa', type: 'standard', from: 'ap-12', to: 'ap-13' },
  { code: 'RT-S02', name: 'Biên Hòa → Cần Thơ', type: 'standard', from: 'ap-13', to: 'ap-14' },
  { code: 'RT-S03', name: 'Tuần tra biển Tây Nam', type: 'tactical', from: 'ap-14', to: 'ap-15' },
  { code: 'RT-S04', name: 'Biên Hòa → Phan Rang (huấn luyện)', type: 'training', from: 'ap-13', to: 'ap-10' },

  { code: 'RT-X01', name: 'Trục Bắc–Nam (Nội Bài → TSN)', type: 'standard', from: 'ap-1', to: 'ap-12' },
  { code: 'RT-X02', name: 'Nội Bài → Đà Nẵng', type: 'standard', from: 'ap-1', to: 'ap-6' },
  { code: 'RT-X03', name: 'Đà Nẵng → Tân Sơn Nhất', type: 'standard', from: 'ap-6', to: 'ap-12' },
]

function haversineKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const R = 6371
  const dLat = toRad(b.lat - a.lat)
  const dLon = toRad(b.lon - a.lon)
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2
  return Math.round(2 * R * Math.asin(Math.sqrt(x)))
}

export const FLIGHT_ROUTES: FlightRoute[] = SEEDS.map((s, idx) => {
  const from = AIRPORTS_BY_ID[s.from]
  const to = AIRPORTS_BY_ID[s.to]
  const distance = haversineKm(from, to)
  const waypoints = s.waypoints ?? [
    { lat: from.lat, lon: from.lon, alt: from.elevation, name: from.code },
    { lat: (from.lat + to.lat) / 2, lon: (from.lon + to.lon) / 2, alt: 6000, name: `WP-${idx + 1}` },
    { lat: to.lat, lon: to.lon, alt: to.elevation, name: to.code },
  ]
  return {
    id: `rt-${idx + 1}`,
    code: s.code,
    name: s.name,
    type: s.type,
    fromAirportId: s.from,
    toAirportId: s.to,
    waypoints,
    distanceKm: distance || 50,
    estimatedDurationMin: Math.max(30, Math.round((distance || 50) / 8)),
  }
})

export const ROUTES_BY_ID: Record<string, FlightRoute> = Object.fromEntries(
  FLIGHT_ROUTES.map((r) => [r.id, r]),
)
